import React from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import AdminSurface from '../../../../components/admin/AdminSurface';
import type { Game } from '../../../../types/features/games';
import { adminCombineSx, adminEmptyStateSx, adminTableSx } from '../../../../styles/adminComponentStyles';
import {
  adminBorders,
  adminColors,
  adminControlHeights,
  adminTypography,
} from '../../../../styles/adminDesignTokens';
import { formatGameReviewCurrency, formatGameReviewValue } from '../utils/formatters/gameReviewFormatters';

interface GameReviewPagination {
  page: number;
  rowsPerPage: number;
  total: number;
  onPageChange: (page: number) => void;
}

interface GameReviewTableProps {
  games: Game[];
  loading: boolean;
  ariaLabel: string;
  emptyTitle: string;
  emptyCopy: string;
  onUpdate: (game: Game) => void;
  pagination?: GameReviewPagination;
}

const updateGameActionSx = {
  appearance: 'none',
  alignItems: 'center',
  backgroundColor: 'transparent',
  border: 0,
  color: 'primary.main',
  cursor: 'pointer',
  display: 'inline-flex',
  fontFamily: 'inherit',
  fontSize: '0.875rem',
  fontWeight: 400,
  gap: 0.5,
  justifyContent: 'center',
  lineHeight: 1.43,
  minWidth: 0,
  p: 0,
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'primary.dark',
  },
  '&:focus-visible': {
    borderRadius: '2px',
    outline: '2px solid rgba(37, 99, 235, 0.35)',
    outlineOffset: '2px',
  },
  '& .MuiSvgIcon-root': {
    flexShrink: 0,
    fontSize: 18,
  },
} as const;

const updateGameActionLabelSx = {
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
} as const;

const gameReviewPaginationSx = {
  borderTop: adminBorders.divider,
  minHeight: adminControlHeights.tablePagination,
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    ...adminTypography.helper,
    color: adminColors.mutedText,
  },
  '& .MuiTablePagination-toolbar': {
    minHeight: adminControlHeights.tablePagination,
  },
} as const;

const GameReviewTable: React.FC<GameReviewTableProps> = ({
  games,
  loading,
  ariaLabel,
  emptyTitle,
  emptyCopy,
  onUpdate,
  pagination,
}) => (
  <AdminSurface sx={{ boxShadow: '0 2px 8px rgb(34 43 69 / 8%)', overflow: 'hidden' }}>
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table
        size="small"
        aria-label={ariaLabel}
        sx={adminCombineSx(adminTableSx, {
          minWidth: 920,
          tableLayout: 'fixed',
          width: '100%',
          '& .MuiTableCell-root': {
            padding: '8px 12px !important',
          },
          '& .MuiTableBody-root .MuiTableRow-root': {
            height: 42,
          },
          '& .MuiTableCell-body': {
            py: 0.75,
          },
        })}
      >
        <TableHead sx={{ '& th': { padding: '8px 12px !important', textTransform: 'uppercase' } }}>
          <TableRow>
            <TableCell>Game ID</TableCell>
            <TableCell>Game Name</TableCell>
            <TableCell>State</TableCell>
            <TableCell>PLU</TableCell>
            <TableCell>Tickets Per Pack</TableCell>
            <TableCell>Cost Per Ticket</TableCell>
            <TableCell align="center">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={7} sx={{ border: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                  <CircularProgress size={28} />
                </Box>
              </TableCell>
            </TableRow>
          ) : games.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} sx={{ border: 0, p: 1.5 }}>
                <Box sx={adminCombineSx(adminEmptyStateSx, { border: 0, minHeight: 96 })}>
                  <Typography className="AdminEmptyState-title">{emptyTitle}</Typography>
                  <Typography className="AdminEmptyState-copy">{emptyCopy}</Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : games.map((game) => (
            <TableRow key={game.id} hover>
              <TableCell>{formatGameReviewValue(game.game_id)}</TableCell>
              <TableCell>{formatGameReviewValue(game.name)}</TableCell>
              <TableCell>{formatGameReviewValue(game.state_code || game.state)}</TableCell>
              <TableCell>{formatGameReviewValue(game.plu)}</TableCell>
              <TableCell>{formatGameReviewValue(game.tickets_per_pack)}</TableCell>
              <TableCell>{formatGameReviewCurrency(game.cost_per_ticket)}</TableCell>
              <TableCell align="center">
                <Box
                  aria-label="Update Game"
                  component="button"
                  type="button"
                  onClick={() => onUpdate(game)}
                  sx={updateGameActionSx}
                >
                  <EditOutlinedIcon aria-hidden="true" />
                  <Box component="span" sx={updateGameActionLabelSx}>
                    Update
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    {pagination && (
      <TablePagination
        component="div"
        count={pagination.total}
        page={Math.max(0, pagination.page - 1)}
        onPageChange={(_, nextPage) => pagination.onPageChange(nextPage + 1)}
        rowsPerPage={pagination.rowsPerPage}
        onRowsPerPageChange={() => {}}
        rowsPerPageOptions={[pagination.rowsPerPage]}
        labelRowsPerPage="Rows per page"
        sx={gameReviewPaginationSx}
      />
    )}
  </AdminSurface>
);

export default React.memo(GameReviewTable);
